<p-dialog
  [header]="headerText()"
  [visible]="visible()"
  [modal]="true"
  [closable]="true"
  [style]="{ width: '24rem' }"
  [breakpoints]="{ '575px': '90vw' }"
  (onHide)="onHide()"
  [pt]="{
    root: { class: 'rounded-xl border border-slate-200 bg-white shadow-card' },
    header: { class: 'flex items-center justify-between gap-4 border-b border-slate-200 px-6 py-4' },
    title: { class: 'font-display text-lg font-semibold text-slate-900' },
    headerActions: { class: 'ml-auto shrink-0' },
    content: { class: 'p-6' },
    mask: { class: 'bg-slate-900/40 backdrop-blur-sm' }
  }"
>
  @if (errorMessage()) {
    <div class="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
      {{ errorMessage() }}
    </div>
  }
  @switch (view()) {
    @case ('login') {
      <form [formGroup]="loginForm" (ngSubmit)="onLoginSubmit()" class="flex flex-col gap-4">
        <div class="flex flex-col gap-2">
          <label for="login-email" class="text-sm font-medium text-slate-700">Email</label>
          <input
            id="login-email"
            type="email"
            formControlName="email"
            placeholder="you@example.com"
            autocomplete="email"
            class="w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            [class.border-red-500]="loginForm.controls.email.invalid && loginForm.controls.email.touched"
          />
          @if (loginForm.controls.email.invalid && loginForm.controls.email.touched) {
            <span class="text-sm text-red-600">Please enter a valid email</span>
          }
        </div>
        <div class="flex flex-col gap-2">
          <label for="login-password" class="text-sm font-medium text-slate-700">Password</label>
          <div class="relative">
            <input
              id="login-password"
              [type]="showLoginPassword() ? 'text' : 'password'"
              formControlName="password"
              placeholder="Enter password"
              autocomplete="current-password"
              class="w-full rounded-lg border border-slate-300 px-3 py-2 pr-10 text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              [class.border-red-500]="loginForm.controls.password.invalid && loginForm.controls.password.touched"
            />
            <button
              type="button"
              (click)="togglePasswordVisibility('login')"
              class="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
              [attr.aria-label]="showLoginPassword() ? 'Hide password' : 'Show password'"
            >
              @if (showLoginPassword()) {
                <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88" />
                </svg>
              } @else {
                <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
                  <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                </svg>
              }
            </button>
          </div>
          @if (loginForm.controls.password.invalid && loginForm.controls.password.touched) {
            <span class="text-sm text-red-600">Password is required</span>
          }
        </div>
        <button
          type="button"
          class="self-start text-sm text-blue-600 hover:text-blue-700 hover:underline"
          (click)="switchView('forgot')"
        >
          Forgot password?
        </button>
        <div class="flex flex-col gap-3 pt-1">
          <p-button
            type="submit"
            label="Sign in"
            styleClass="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5"
            [disabled]="loginForm.invalid || submitting()"
            [loading]="submitting()"
          />
          <p class="text-center text-sm text-slate-600">
            Don't have an account?
            <button
              type="button"
              class="ml-1 font-medium text-blue-600 hover:text-blue-700 hover:underline focus:outline-none focus:underline"
              (click)="switchView('register')"
            >
              Create account
            </button>
          </p>
        </div>
      </form>
    }
    @case ('register') {
      <form [formGroup]="registerForm" (ngSubmit)="onRegisterSubmit()" class="flex flex-col gap-4">
        <div class="flex flex-col gap-2">
          <label for="register-email" class="text-sm font-medium text-slate-700">Email</label>
          <input
            id="register-email"
            type="email"
            formControlName="email"
            placeholder="you@example.com"
            autocomplete="email"
            class="w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            [class.border-red-500]="registerForm.controls.email.invalid && registerForm.controls.email.touched"
          />
          @if (registerForm.controls.email.invalid && registerForm.controls.email.touched) {
            <span class="text-sm text-red-600">Please enter a valid email</span>
          }
        </div>
        <div class="flex flex-col gap-2">
          <label for="register-password" class="text-sm font-medium text-slate-700">Password</label>
          <div class="relative">
            <input
              id="register-password"
              [type]="showRegisterPassword() ? 'text' : 'password'"
              formControlName="password"
              placeholder="At least 8 characters"
              autocomplete="new-password"
              class="w-full rounded-lg border border-slate-300 px-3 py-2 pr-10 text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              [class.border-red-500]="registerForm.controls.password.invalid && registerForm.controls.password.touched"
            />
            <button
              type="button"
              (click)="togglePasswordVisibility('register')"
              class="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
              [attr.aria-label]="showRegisterPassword() ? 'Hide password' : 'Show password'"
            >
              @if (showRegisterPassword()) {
                <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88" />
                </svg>
              } @else {
                <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
                  <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                </svg>
              }
            </button>
          </div>
          @if (registerForm.controls.password.invalid && registerForm.controls.password.touched) {
            <span class="text-sm text-red-600">Password must be at least 8 characters</span>
          }
        </div>
        <div class="flex flex-col gap-2">
          <label for="register-confirm" class="text-sm font-medium text-slate-700">Confirm password</label>
          <div class="relative">
            <input
              id="register-confirm"
              [type]="showRegisterConfirmPassword() ? 'text' : 'password'"
              formControlName="confirmPassword"
              placeholder="Confirm password"
              autocomplete="new-password"
              class="w-full rounded-lg border border-slate-300 px-3 py-2 pr-10 text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              [class.border-red-500]="registerForm.controls.confirmPassword.errors?.['mismatch']"
            />
            <button
              type="button"
              (click)="togglePasswordVisibility('registerConfirm')"
              class="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
              [attr.aria-label]="showRegisterConfirmPassword() ? 'Hide password' : 'Show password'"
            >
              @if (showRegisterConfirmPassword()) {
                <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88" />
                </svg>
              } @else {
                <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
                  <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                </svg>
              }
            </button>
          </div>
          @if (registerForm.controls.confirmPassword.errors?.['mismatch']) {
            <span class="text-sm text-red-600">Passwords do not match</span>
          }
        </div>
        <div class="flex flex-col gap-3 pt-1">
          <p-button
            type="submit"
            label="Create account"
            styleClass="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5"
            [disabled]="registerForm.invalid || submitting()"
            [loading]="submitting()"
          />
          <p class="text-center text-sm text-slate-600">
            Already have an account?
            <button
              type="button"
              class="ml-1 font-medium text-blue-600 hover:text-blue-700 hover:underline focus:outline-none focus:underline"
              (click)="switchView('login')"
            >
              Sign in
            </button>
          </p>
        </div>
      </form>
    }
    @case ('forgot') {
      <form [formGroup]="forgotForm" (ngSubmit)="onForgotSubmit()" class="flex flex-col gap-4">
        <p class="text-sm text-slate-600">
          Enter your email and we'll send you a link to reset your password.
        </p>
        <div class="flex flex-col gap-2">
          <label for="forgot-email" class="text-sm font-medium text-slate-700">Email</label>
          <input
            id="forgot-email"
            type="email"
            formControlName="email"
            placeholder="you@example.com"
            autocomplete="email"
            class="w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            [class.border-red-500]="forgotForm.controls.email.invalid && forgotForm.controls.email.touched"
          />
          @if (forgotForm.controls.email.invalid && forgotForm.controls.email.touched) {
            <span class="text-sm text-red-600">Please enter a valid email</span>
          }
        </div>
        <div class="flex flex-col gap-3 pt-1">
          <p-button
            type="submit"
            label="Send reset link"
            styleClass="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5"
            [disabled]="forgotForm.invalid || submitting()"
            [loading]="submitting()"
          />
          <button
            type="button"
            class="text-sm text-blue-600 hover:text-blue-700 hover:underline focus:outline-none focus:underline"
            (click)="switchView('login')"
          >
            Back to sign in
          </button>
        </div>
      </form>
    }
    @case ('confirmRegister') {
      <form [formGroup]="confirmRegisterForm" (ngSubmit)="onConfirmRegisterSubmit()" class="flex flex-col gap-4">
        <p class="text-sm text-slate-600">
          We sent a verification code to {{ pendingEmail() }}. Enter it below.
        </p>
        <div class="flex flex-col gap-2">
          <label for="confirm-code" class="text-sm font-medium text-slate-700">Verification code</label>
          <input
            id="confirm-code"
            type="text"
            formControlName="code"
            placeholder="123456"
            autocomplete="one-time-code"
            class="w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            [class.border-red-500]="confirmRegisterForm.controls.code.invalid && confirmRegisterForm.controls.code.touched"
          />
          @if (confirmRegisterForm.controls.code.invalid && confirmRegisterForm.controls.code.touched) {
            <span class="text-sm text-red-600">Please enter the 6-digit code</span>
          }
        </div>
        <div class="flex flex-col gap-3 pt-1">
          <p-button
            type="submit"
            label="Verify"
            styleClass="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5"
            [disabled]="confirmRegisterForm.invalid || submitting()"
            [loading]="submitting()"
          />
          <button
            type="button"
            class="text-sm text-blue-600 hover:text-blue-700 hover:underline focus:outline-none focus:underline"
            (click)="resendCode()"
            [disabled]="submitting()"
          >
            Resend code
          </button>
          <button
            type="button"
            class="text-sm text-slate-600 hover:text-slate-800 focus:outline-none focus:underline"
            (click)="switchView('login')"
          >
            Back to sign in
          </button>
        </div>
      </form>
    }
    @case ('confirmForgot') {
      <form [formGroup]="confirmForgotForm" (ngSubmit)="onConfirmForgotSubmit()" class="flex flex-col gap-4">
        <p class="text-sm text-slate-600">
          Enter the code we sent to {{ pendingEmail() }} and your new password.
        </p>
        <div class="flex flex-col gap-2">
          <label for="forgot-code" class="text-sm font-medium text-slate-700">Verification code</label>
          <input
            id="forgot-code"
            type="text"
            formControlName="code"
            placeholder="123456"
            autocomplete="one-time-code"
            class="w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            [class.border-red-500]="confirmForgotForm.controls.code.invalid && confirmForgotForm.controls.code.touched"
          />
        </div>
        <div class="flex flex-col gap-2">
          <label for="forgot-new-password" class="text-sm font-medium text-slate-700">New password</label>
          <div class="relative">
            <input
              id="forgot-new-password"
              [type]="showConfirmForgotPassword() ? 'text' : 'password'"
              formControlName="newPassword"
              placeholder="At least 8 characters"
              autocomplete="new-password"
              class="w-full rounded-lg border border-slate-300 px-3 py-2 pr-10 text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <button
              type="button"
              (click)="togglePasswordVisibility('confirmForgot')"
              class="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-slate-500 hover:bg-slate-100"
              aria-label="Toggle password visibility"
            >
              <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
                <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
              </svg>
            </button>
          </div>
        </div>
        <div class="flex flex-col gap-2">
          <label for="forgot-confirm-password" class="text-sm font-medium text-slate-700">Confirm new password</label>
          <div class="relative">
            <input
              id="forgot-confirm-password"
              [type]="showConfirmForgotConfirm() ? 'text' : 'password'"
              formControlName="confirmPassword"
              placeholder="Confirm password"
              autocomplete="new-password"
              class="w-full rounded-lg border border-slate-300 px-3 py-2 pr-10 text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <button
              type="button"
              (click)="togglePasswordVisibility('confirmForgotConfirm')"
              class="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-slate-500 hover:bg-slate-100"
              aria-label="Toggle password visibility"
            >
              <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
                <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
              </svg>
            </button>
          </div>
          @if (confirmForgotForm.controls.confirmPassword.errors?.['mismatch']) {
            <span class="text-sm text-red-600">Passwords do not match</span>
          }
        </div>
        <div class="flex flex-col gap-3 pt-1">
          <p-button
            type="submit"
            label="Reset password"
            styleClass="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5"
            [disabled]="confirmForgotForm.invalid || submitting()"
            [loading]="submitting()"
          />
          <button
            type="button"
            class="text-sm text-blue-600 hover:text-blue-700 hover:underline focus:outline-none focus:underline"
            (click)="switchView('forgot')"
          >
            Back
          </button>
        </div>
      </form>
    }
  }
</p-dialog>
